import React from 'react';
import { LayoutGrid, RefreshCw, Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { featureSettingsApi, type FeatureSettingsDto } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { clearAddressAutocompleteSettingsCache } from '../../../hooks/useGeoapifyAddressAutocomplete';
import { fachBezeichnungenMerken } from '../../../lib/settings';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';

type FeatureSettingsFormValues = Omit<FeatureSettingsDto, 'googlePlacesApiKey' | 'googlePlacesApiKeyIsSet'>;

const defaultSettings: FeatureSettingsFormValues = {
  timeInterval: '15',
  defaultView: 'calendar',
  enableContextMenu: true,
  allowRoomBooking: true,
  allowTrialLessons: true,
  maxBookingDays: '60',
  subjectSingularLabel: 'Fach',
  subjectPluralLabel: 'Fächer',
  chatEnabled: true,
  loginEnabled: true,
};

export default function FeatureSettings() {
  // ADM-19.3: Bezeichnung „Fach“ ist eine Systemeinstellung; die übrigen Schalter
  // gehören zu den öffentlichen Funktionen. Ohne Recht nur ansehen.
  const darfSystem = usePermission(P.admin.system_und_schnittstellen.systemeinstellungen_bearbeiten);
  const darfSpeichern = usePermission(P.admin.oeffentliche_bereiche.oeffentliche_funktionen_de_aktivieren);
  const [isPageLoading, setIsPageLoading] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [apiKeyInput, setApiKeyInput] = React.useState('');
  const [apiKeyIsSet, setApiKeyIsSet] = React.useState(false);
  const [lastLoadedSettings, setLastLoadedSettings] = React.useState<FeatureSettingsFormValues>(defaultSettings);

  const form = useForm<FeatureSettingsFormValues>({
    defaultValues: defaultSettings,
  });

  const loadSettings = React.useCallback(async () => {
    setIsPageLoading(true);
    try {
      const data = await featureSettingsApi.get();
      const nextValues: FeatureSettingsFormValues = {
        timeInterval: String(data.timeInterval ?? defaultSettings.timeInterval),
        defaultView: String(data.defaultView ?? defaultSettings.defaultView),
        enableContextMenu: Boolean(data.enableContextMenu),
        allowRoomBooking: Boolean(data.allowRoomBooking),
        allowTrialLessons: Boolean(data.allowTrialLessons),
        maxBookingDays: String(data.maxBookingDays ?? defaultSettings.maxBookingDays),
        subjectSingularLabel: String(data.subjectSingularLabel ?? defaultSettings.subjectSingularLabel),
        subjectPluralLabel: String(data.subjectPluralLabel ?? defaultSettings.subjectPluralLabel),
        chatEnabled: data.chatEnabled !== false,
        loginEnabled: data.loginEnabled !== false,
      };
      form.reset(nextValues);
      setLastLoadedSettings(nextValues);
      setApiKeyInput('');
      setApiKeyIsSet(Boolean(data.googlePlacesApiKeyIsSet));
    } catch (error) {
      toast.error('Fehler beim Laden der Funktionseinstellungen');
      console.error(error);
    } finally {
      setIsPageLoading(false);
    }
  }, [form]);

  React.useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const [apiKeyEntfernen, setApiKeyEntfernen] = React.useState(false);

  const onSubmit = async (data: FeatureSettingsFormValues) => {
    setIsLoading(true);
    try {
      const nextApiKey = apiKeyInput.trim();
      // „***“ heißt für den Server „Schlüssel behalten“, leer „entfernen“ (T-0029).
      const payload: FeatureSettingsDto = {
        ...data,
        googlePlacesApiKey: apiKeyEntfernen ? '' : nextApiKey === '' ? '***' : nextApiKey,
      };
      const saved = await featureSettingsApi.update(payload);
      const nextValues: FeatureSettingsFormValues = {
        timeInterval: String(saved.timeInterval ?? defaultSettings.timeInterval),
        defaultView: String(saved.defaultView ?? defaultSettings.defaultView),
        enableContextMenu: Boolean(saved.enableContextMenu),
        allowRoomBooking: Boolean(saved.allowRoomBooking),
        allowTrialLessons: Boolean(saved.allowTrialLessons),
        maxBookingDays: String(saved.maxBookingDays ?? defaultSettings.maxBookingDays),
        subjectSingularLabel: String(saved.subjectSingularLabel ?? defaultSettings.subjectSingularLabel),
        subjectPluralLabel: String(saved.subjectPluralLabel ?? defaultSettings.subjectPluralLabel),
        chatEnabled: saved.chatEnabled !== false,
        loginEnabled: saved.loginEnabled !== false,
      };
      form.reset(nextValues);
      setLastLoadedSettings(nextValues);
      setApiKeyInput('');
      setApiKeyEntfernen(false);
      setApiKeyIsSet(Boolean(saved.googlePlacesApiKeyIsSet));
      clearAddressAutocompleteSettingsCache();
      fachBezeichnungenMerken(nextValues.subjectSingularLabel, nextValues.subjectPluralLabel);
      toast.success('Einstellungen wurden gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellungen');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    form.reset(lastLoadedSettings);
    setApiKeyInput('');
    toast.info('Einstellungen wurden zurückgesetzt');
  };

  if (isPageLoading) {
    return <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Funktionseinstellungen</h1>
          <p className="text-muted-foreground">Aktivieren oder deaktivieren Sie Funktionen im System.</p>
        </div>
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">Lade Einstellungen...</CardContent>
        </Card>
      </div>;
  }

  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Funktionseinstellungen</h1>
        <p className="text-muted-foreground">Aktivieren oder deaktivieren Sie Funktionen im System.</p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <LayoutGrid className="mr-2 h-5 w-5" />
                Funktionen
              </CardTitle>
              <CardDescription>Steuert Verhaltensoptionen im Office-Bereich.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="subjectSingularLabel" render={({ field }) => <FormItem>
                    <FormLabel>Bezeichnung Fach (Singular)</FormLabel>
                    <FormControl>
                      <Input {...field} uiSize="md" placeholder="z. B. Fach" disabled={!darfSystem || !darfSpeichern} />
                    </FormControl>
                    <FormDescription>Wird u. a. in Personenliste und -details angezeigt.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="subjectPluralLabel" render={({ field }) => <FormItem>
                    <FormLabel>Bezeichnung Fächer (Plural)</FormLabel>
                    <FormControl>
                      <Input {...field} uiSize="md" placeholder="z. B. Fächer" disabled={!darfSystem || !darfSpeichern} />
                    </FormControl>
                    <FormDescription>Wird u. a. in Listen und Titeln verwendet.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              <FormItem>
                <FormLabel>Google Places API Key</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    uiSize="md"
                    placeholder={apiKeyIsSet ? '***' : 'AIza...'}
                    value={apiKeyInput}
                    onChange={(event) => setApiKeyInput(event.target.value)}
                    disabled={!darfSpeichern}
                  />
                </FormControl>
                <FormDescription>
                  {apiKeyIsSet
                    ? 'Ein Schlüssel ist hinterlegt. Leer lassen, um ihn zu behalten.'
                    : 'Kein Schlüssel hinterlegt – die Adress-Suche bietet dann keine Vorschläge an.'}
                </FormDescription>
                {apiKeyIsSet ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={apiKeyEntfernen}
                      onChange={(event) => setApiKeyEntfernen(event.target.checked)}
                      disabled={!darfSpeichern}
                    />
                    Schlüssel beim Speichern entfernen
                  </label>
                ) : null}
              </FormItem>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <LayoutGrid className="mr-2 h-5 w-5" />
                Website
              </CardTitle>
              <CardDescription>Funktionen der öffentlichen Website ein-/ausblenden.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="chatEnabled" render={({ field }) => <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">KI-Chat anzeigen</FormLabel>
                      <FormDescription>Blendet das KI-Chat-Widget auf der Website ein oder aus.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!darfSpeichern} />
                    </FormControl>
                  </FormItem>} />
              <FormField control={form.control} name="loginEnabled" render={({ field }) => <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Kunden-Login anzeigen</FormLabel>
                      <FormDescription>Blendet den Login-/Kundenbereich-Link in der Website-Navigation ein oder aus.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!darfSpeichern} />
                    </FormControl>
                  </FormItem>} />
            </CardContent>
          </Card>
          <div className="flex justify-end space-x-4">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleReset} disabled={isLoading || !darfSpeichern}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Zurücksetzen
              </Button>
              <Button type="submit" disabled={isLoading || !darfSpeichern}>
                {isLoading ? <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Speichern...
                  </> : <>
                    <Save className="mr-2 h-4 w-4" />
                    Einstellungen speichern
                  </>}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>;
}


